import { ReadOutlined, ReloadOutlined } from '@ant-design/icons'
import {
	Button,
	Form,
	Input,
	InputNumber,
	Modal,
	Space,
	Switch,
	Table,
	Tag,
	message,
} from 'antd'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ListPageIntro, ListPageShell } from '../../common'
import {
	listCategoriesForIntro,
	saveCategoryIntro,
} from '../../services/courseCategoryIntro.service'
import './CategoryIntroList.scss'

const { TextArea } = Input

const emptyLocale = () => ({
	introTitle: '',
	introHighlight: '',
	introBody: '',
	introMore: '',
})

export const CategoryIntroList = () => {
	const [items, setItems] = useState([])
	const [total, setTotal] = useState(0)
	const [enabledCount, setEnabledCount] = useState(0)
	const [loading, setLoading] = useState(false)
	const [query, setQuery] = useState('')
	const [introOnly, setIntroOnly] = useState(false)
	const [editing, setEditing] = useState(null)
	const [saving, setSaving] = useState(false)
	const [form] = Form.useForm()
	const mountedRef = useRef(true)
	const requestSeq = useRef(0)

	useEffect(() => {
		mountedRef.current = true
		return () => {
			mountedRef.current = false
		}
	}, [])

	const load = useCallback(async () => {
		const seq = ++requestSeq.current
		setLoading(true)
		try {
			const rs = await listCategoriesForIntro({
				q: query.trim() || undefined,
				introOnly,
			})
			if (!mountedRef.current || seq !== requestSeq.current) return
			setItems(Array.isArray(rs?.data) ? rs.data : [])
			setTotal(Number(rs?.total) || 0)
			setEnabledCount(Number(rs?.enabledCount) || 0)
		} catch (err) {
			if (!mountedRef.current || seq !== requestSeq.current) return
			message.error(err?.message || 'Could not load categories.')
		} finally {
			if (mountedRef.current && seq === requestSeq.current) {
				setLoading(false)
			}
		}
	}, [query, introOnly])

	useEffect(() => {
		load()
	}, [load])

	const openEdit = useCallback(
		record => {
			setEditing(record)
			form.setFieldsValue({
				introEnabled: Boolean(record.introEnabled),
				coverImageUrl: record.coverImageUrl || '',
				authorName: record.authorName || '',
				authorUserID: record.authorUserID ?? null,
				en: { ...emptyLocale(), ...(record.en || {}) },
				vi: { ...emptyLocale(), ...(record.vi || {}) },
			})
		},
		[form]
	)

	const closeEdit = useCallback(() => {
		setEditing(null)
		form.resetFields()
	}, [form])

	const onSave = useCallback(async () => {
		if (!editing) return
		try {
			const values = await form.validateFields()
			setSaving(true)
			await saveCategoryIntro(editing.id, values)
			message.success('Intro saved.')
			closeEdit()
			await load()
		} catch (err) {
			if (err?.errorFields) return
			message.error(err?.message || 'Save failed.')
		} finally {
			if (mountedRef.current) setSaving(false)
		}
	}, [editing, form, closeEdit, load])

	const columns = useMemo(
		() => [
			{
				title: 'ID',
				dataIndex: 'id',
				width: 72,
			},
			{
				title: 'Category',
				key: 'name',
				render: (_, r) => (
					<div>
						<div>{r.nameEn}</div>
						<div className="category-intro-list__muted">{r.nameVi}</div>
						{r.isRoot ? (
							<Tag>Root / umbrella</Tag>
						) : (
							<span className="category-intro-list__muted">
								Parent: {r.parentNameEn || '—'}
							</span>
						)}
					</div>
				),
			},
			{
				title: 'Cover',
				dataIndex: 'coverImageUrl',
				width: 72,
				render: url =>
					url ? (
						<img
							src={
								url.startsWith('http')
									? url
									: `https://app.wordsmine.com${url}`
							}
							alt=""
							className="category-intro-list__cover-thumb"
						/>
					) : (
						<span className="category-intro-list__muted">—</span>
					),
			},
			{
				title: 'Intro',
				dataIndex: 'introEnabled',
				width: 100,
				render: v =>
					v ? <Tag color="blue">Enabled</Tag> : <Tag>Off</Tag>,
			},
			{
				title: 'Author',
				key: 'author',
				width: 160,
				render: (_, r) =>
					r.authorName ? (
						<span>
							{r.authorName}
							{r.authorUserID != null ? (
								<span className="category-intro-list__muted">
									{' '}
									(#{r.authorUserID})
								</span>
							) : null}
						</span>
					) : (
						<span className="category-intro-list__muted">—</span>
					),
			},
			{
				title: '',
				key: 'actions',
				width: 90,
				render: (_, r) => (
					<Button type="link" onClick={() => openEdit(r)}>
						Edit
					</Button>
				),
			},
		],
		[openEdit]
	)

	return (
		<ListPageShell className="category-intro-list">
			<ListPageIntro
				icon={ReadOutlined}
				title="Category Intro"
				subtitle="Manage Book / Course introduction banners (cover, author, VI/EN copy). Enabled intros sync to webapp and mobile. Put intro on the selected category, or on the umbrella root so children inherit it."
			/>

			<div className="category-intro-list__stats">
				{enabledCount} enabled · {total} shown
			</div>

			<div className="category-intro-list__toolbar">
				<Input.Search
					allowClear
					placeholder="Search name or id"
					style={{ maxWidth: 280 }}
					onSearch={v => setQuery(v || '')}
					onChange={e => {
						if (!e.target.value) setQuery('')
					}}
				/>
				<Space>
					<span>Intro only</span>
					<Switch checked={introOnly} onChange={setIntroOnly} />
				</Space>
				<Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
					Refresh
				</Button>
			</div>

			<Table
				rowKey="id"
				loading={loading}
				columns={columns}
				dataSource={items}
				pagination={{ pageSize: 50, showSizeChanger: true }}
				scroll={{ x: 900 }}
			/>

			<Modal
				title={
					editing
						? `Edit intro · #${editing.id} · ${editing.nameEn}`
						: 'Edit intro'
				}
				open={Boolean(editing)}
				onCancel={closeEdit}
				onOk={onSave}
				okText="Save"
				confirmLoading={saving}
				width={720}
				destroyOnClose
			>
				<Form form={form} layout="vertical">
					<Form.Item
						name="introEnabled"
						label="Show intro banner"
						valuePropName="checked"
					>
						<Switch />
					</Form.Item>
					<Form.Item
						name="coverImageUrl"
						label="Cover image URL"
						extra="https://… or root-relative /course-intro/….jpg"
					>
						<Input placeholder="/course-intro/BannerMarty.jpg" />
					</Form.Item>
					<Form.Item name="authorName" label="Author display name">
						<Input placeholder="wordsmine_official" />
					</Form.Item>
					<Form.Item
						name="authorUserID"
						label="Author user ID (optional profile link)"
						extra="Leave empty for plain text; set users.id to link profile."
					>
						<InputNumber min={1} style={{ width: '100%' }} />
					</Form.Item>

					<div className="category-intro-list__form-section">English</div>
					<Form.Item name={['en', 'introTitle']} label="Title">
						<Input />
					</Form.Item>
					<Form.Item name={['en', 'introHighlight']} label="Highlight">
						<Input />
					</Form.Item>
					<Form.Item name={['en', 'introBody']} label="Body (HTML allowed)">
						<TextArea rows={5} />
					</Form.Item>
					<Form.Item name={['en', 'introMore']} label="More (collapsible)">
						<TextArea rows={3} />
					</Form.Item>

					<div className="category-intro-list__form-section">Vietnamese</div>
					<Form.Item name={['vi', 'introTitle']} label="Title">
						<Input />
					</Form.Item>
					<Form.Item name={['vi', 'introHighlight']} label="Highlight">
						<Input />
					</Form.Item>
					<Form.Item name={['vi', 'introBody']} label="Body (HTML allowed)">
						<TextArea rows={5} />
					</Form.Item>
					<Form.Item name={['vi', 'introMore']} label="More (collapsible)">
						<TextArea rows={3} />
					</Form.Item>
				</Form>
			</Modal>
		</ListPageShell>
	)
}
